import dayjs from 'dayjs';

import { MaybeUndef } from 'types/UtilityTypes';
import { SuggestedQuoteChange } from 'types/suggestions/QuoteSuggestions';
import { formatCurrency } from 'utils/formatCurrency';

import { QuoteHandledReason, QuoteHandledState } from '../types';

export type HandledTooltipFields = Pick<
  SuggestedQuoteChange,
  'createdAt' | 'handledReason' | 'handledAt' | 'handledByName' | 'quotedAmount'
>;

export type HandledTooltipDetails = {
  headline: string;
  handledByName: MaybeUndef<string>;
  handledDate: string;
};

/**
 * Formats the hover text for a handled lane.
 * Missing fields are omitted. Times use the rep's local timezone.
 */
export function formatHandledTooltip(
  state: QuoteHandledState,
  fields: HandledTooltipFields
): string {
  const { headline, handledByName, handledDate } = getHandledTooltipDetails(
    state,
    fields
  );

  if (
    state === QuoteHandledState.Skipped &&
    fields.handledReason !== QuoteHandledReason.Stale
  ) {
    return [
      handledByName ? `${headline} by ${handledByName}` : headline,
      handledDate,
    ]
      .filter(Boolean)
      .join(' · ');
  }

  return [headline, handledDate, handledByName].filter(Boolean).join(' · ');
}

export function getHandledTooltipDetails(
  state: QuoteHandledState,
  {
    createdAt,
    handledReason,
    handledAt,
    handledByName,
    quotedAmount,
  }: HandledTooltipFields
): HandledTooltipDetails {
  const isStale =
    state === QuoteHandledState.Skipped &&
    handledReason === QuoteHandledReason.Stale;

  return {
    headline: getHeadline(state, handledReason, quotedAmount),
    handledDate: isStale
      ? formatRequestedDate(createdAt)
      : formatHandledAt(handledAt),
    handledByName,
  };
}

function getHeadline(
  state: QuoteHandledState,
  handledReason: MaybeUndef<QuoteHandledReason>,
  quotedAmount: MaybeUndef<number>
): string {
  const amount =
    quotedAmount != null ? ` ${formatCurrency(quotedAmount, 'USD')}` : '';

  switch (state) {
    case QuoteHandledState.Replied:
      return handledReason === QuoteHandledReason.SiblingReply
        ? `Teammate replied${amount}`
        : `Replied${amount}`;
    case QuoteHandledState.Quoted:
      return `Quoted${amount}`;
    case QuoteHandledState.Skipped:
      return handledReason === QuoteHandledReason.Stale
        ? 'No reply found'
        : 'Dismissed';
    default:
      return 'Not handled yet';
  }
}

// "Sep 24, 3:10 PM" in the rep's timezone; empty when the time is missing or invalid.
export function formatHandledAt(handledAt: MaybeUndef<string>): string {
  return handledAt && dayjs(handledAt).isValid()
    ? dayjs(handledAt).format('MMM D, h:mm A')
    : '';
}

function formatRequestedDate(createdAt: string): string {
  return dayjs(createdAt).isValid()
    ? `Requested ${dayjs(createdAt).format('MMM D')}`
    : '';
}
